import { memo, useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { useRecoilValue } from 'recoil';
import { PATH_ARROW_LAYER_STYLE, PATH_LAYER_STYLE, PATH_POINTS_LAYER_STYLE } from '../../../constants/map';
import { useCamera } from '../../../hooks/useCamera';
import { timelineRecordPathsLngLatsState, timelineRecordPathsState } from '../../../storage/streaming';

function CapturingVideoPathsLayer() {
  const { map } = useMap();
  const { fitToLngLats, zoomTo } = useCamera();
  const lngLats = useRecoilValue(timelineRecordPathsLngLatsState);
  const paths = useRecoilValue(timelineRecordPathsState);

  useEffect(() => {
    if (!map) return;
    const arrowUrl = '/img/icons/map/arrow.png';
    map.loadImage(`${arrowUrl}`, (error, image) => {
      if (error) {
        return;
      }
      if (image && !map.hasImage('arrow')) {
        map.addImage('arrow', image);
      }
    });
  }, [map]);

  useEffect(() => {
    if (!lngLats) return;
    if (lngLats.length > 1) {
      fitToLngLats(lngLats);
    } else {
      zoomTo({
        lng: lngLats[0][0],
        lat: lngLats[0][1],
      });
    }
  }, [lngLats, zoomTo, fitToLngLats]);

  const lineStrings = useMemo((): (GeoJSON.LineString & { properties: { uuid: string } })[] | undefined => {
    return paths?.map((path) => ({
      type: 'LineString',
      coordinates: path.lineStringCoordinates as number[][],
      properties: {
        uuid: path.uuid,
      },
    }));
  }, [paths]);

  if (!lineStrings || lineStrings.length === 0 || !paths) return null;

  return (
    <>
      {lineStrings.map((lineString, i) => (
        <LineStringLayer
          key={`source-${i}`}
          index={i}
          lineString={lineString}
        />
      ))}
    </>
  );
}

const LineStringLayer = memo(
  ({ lineString, index }: { lineString: GeoJSON.LineString & { properties: { uuid: string } }; index: number }) => {
    // const eventHovered = useRecoilValue(eventHoveredState);
    // const isHover = useRecoilValue(timelineEventIsHoverState(lineString.properties.uuid));

    // if (!isHover && eventHovered) return null;

    return (
      <Source
        type="geojson"
        data={lineString}
        id={`path-source-${index}`}
      >
        <Layer
          {...PATH_LAYER_STYLE}
          id={`path-layer-${index}`}
          paint={{
            'line-color': '#000',
            'line-width': 8,
          }}
        />
        <Layer
          {...PATH_ARROW_LAYER_STYLE}
          id={`path-arrow-layer-${index}`}
        />
        <Layer
          {...PATH_POINTS_LAYER_STYLE}
          id={`path-points-layer-${index}`}
          paint={{
            'circle-color': '#ffffff',
            'circle-radius': 2.5,
            'circle-stroke-color': '#000000',
            'circle-stroke-width': 4,
          }}
        />
      </Source>
    );
  },
);

export default memo(CapturingVideoPathsLayer);
